import { Injectable } from '@angular/core';
import { parseFixedOffsetTimeZone, parseShortOffsetMinutes } from '../../helpers/timezone-offset-parser.helper';

@Injectable({
  providedIn: 'root',
})
export class TimeZoneHelper {
  /**
   * Resolves the UTC offset (in minutes) of an IANA time zone id (e.g. "Europe/Berlin")
   * at a given instant, using Intl.DateTimeFormat's 'shortOffset'. An IANA zone id does not have
   * a single constant offset — it depends on the date (DST) — so a `referenceDate` is required.
   * A legacy fixed offset (e.g. "+0330") is a constant with no DST and is returned as-is,
   * without going through Intl (browsers do not reliably accept offset strings as `timeZone`).
   *
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @param referenceDate Instant to resolve the offset for (defaults to now), since the
   * offset can vary across the year due to DST. Ignored for fixed offsets.
   * @returns UTC offset in minutes for `timeZone` at `referenceDate`, or 0 if it cannot be resolved.
   */
  GetIanaTimeZoneOffsetInMinutes(timeZone: string, referenceDate: Date = new Date()): number {
    const fixedOffsetMinutes = parseFixedOffsetTimeZone(timeZone);

    if (fixedOffsetMinutes !== null) {
      return fixedOffsetMinutes;
    }

    const formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' });
    const offsetPart = formatter.formatToParts(referenceDate).find(part => part.type === 'timeZoneName');

    if (!offsetPart) {
      return 0;
    }

    return parseShortOffsetMinutes(offsetPart.value);
  }

  /**
   * Reads the wall-clock date/time components that `timeZone` actually displays at a given
   * instant. Mirrors the parsing pattern used by `CaDatePipe.toZonedFakeUtcDate` (formatToParts
   * with hourCycle 'h23'), reused here so {@link ResolveDstGap} can compare an intended
   * wall-clock time against what the zone actually renders for a candidate instant.
   *
   * @param instant The instant to resolve wall-clock components for.
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330"
   * (resolved arithmetically as instant + offset, without Intl).
   * @returns The year/month/day/hour/minute/second displayed in `timeZone` at `instant`.
   */
  GetZonedDateComponents(
    instant: Date,
    timeZone: string
  ): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
    const fixedOffsetMinutes = parseFixedOffsetTimeZone(timeZone);

    if (fixedOffsetMinutes !== null) {
      const shifted = new Date(instant.getTime() + fixedOffsetMinutes * 60000);

      return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
        hour: shifted.getUTCHours(),
        minute: shifted.getUTCMinutes(),
        second: shifted.getUTCSeconds(),
      };
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const parts = formatter.formatToParts(instant).reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {} as Record<string, string>);

    return {
      year: Number(parts['year']),
      month: Number(parts['month']),
      day: Number(parts['day']),
      hour: Number(parts['hour']),
      minute: Number(parts['minute']),
      second: Number(parts['second']),
    };
  }

  /**
   * Resolves a wall-clock date/time the caller intends in `timeZone` to a real UTC instant,
   * detecting and correcting "spring forward" DST gaps.
   *
   * During a spring-forward DST transition (e.g. Europe/Berlin on 2026-03-29, clocks jump from
   * 02:00 to 03:00), wall-clock times inside the gap (02:00:00–02:59:59 that day) do not exist
   * in that zone. Building a `Date` naively from such components (e.g.
   * `new Date(year, month - 1, day, hour, minute, second)`) produces undefined, engine-dependent
   * behavior. This method instead resolves the intended components against the zone's actual
   * DST transition and, if they fall inside a gap, deterministically shifts the requested time
   * forward by the size of the gap.
   *
   * Algorithm: treat the requested components as a naive UTC anchor (`utcGuess`), then look up
   * the zone's offset at that anchor (`offsetA`) and use it to derive a first candidate instant.
   * Look up the offset at that candidate (`offsetB`); if it agrees with `offsetA`, the candidate
   * is self-consistent (the offset used to derive it matches the offset actually in effect there)
   * and is returned as-is. Otherwise derive a second candidate from `offsetB` and check it the
   * same way against a fresh offset (`offsetC`); if self-consistent, it is verified by comparing
   * its actual wall-clock rendering back against the requested components before accepting it
   * (this guards the fall-back/ambiguous-time edge case, which is out of scope, so it must not be
   * misreported as a rounded gap). If neither candidate is a self-consistent match, the requested
   * time falls in a genuine spring-forward gap, and is resolved using the smaller of `offsetA`/
   * `offsetB` — the standard (pre-transition) offset, since DST offsets are always larger than
   * standard offsets regardless of the zone's sign — rounding forward to the first valid instant.
   *
   * @param components The wall-clock date/time (year, 1-based month, day, hour, minute, second)
   * the caller intends in `timeZone`.
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @returns `date`: the resolved UTC instant (pushed past the gap if one was detected).
   * `wasRounded`: true if the requested wall-clock time fell in a spring-forward DST gap and
   * was rounded forward; false if the requested time was valid as-is.
   */
  ResolveDstGap(
    components: { year: number; month: number; day: number; hour: number; minute: number; second: number },
    timeZone: string
  ): { date: Date; wasRounded: boolean } {
    const utcGuess = Date.UTC(
      components.year,
      components.month - 1,
      components.day,
      components.hour,
      components.minute,
      components.second
    );

    const offsetA = this.GetIanaTimeZoneOffsetInMinutes(timeZone, new Date(utcGuess));
    const candidate1Ms = utcGuess - offsetA * 60000;
    const offsetB = this.GetIanaTimeZoneOffsetInMinutes(timeZone, new Date(candidate1Ms));

    if (offsetA === offsetB) {
      return { date: new Date(candidate1Ms), wasRounded: false };
    }

    const candidate2Ms = utcGuess - offsetB * 60000;
    const offsetC = this.GetIanaTimeZoneOffsetInMinutes(timeZone, new Date(candidate2Ms));

    if (offsetB === offsetC) {
      const actualComponents = this.GetZonedDateComponents(new Date(candidate2Ms), timeZone);

      const matches =
        actualComponents.year === components.year &&
        actualComponents.month === components.month &&
        actualComponents.day === components.day &&
        actualComponents.hour === components.hour &&
        actualComponents.minute === components.minute &&
        actualComponents.second === components.second;

      if (matches) {
        return { date: new Date(candidate2Ms), wasRounded: false };
      }
    }

    const minOffset = Math.min(offsetA, offsetB);
    const correctedMs = utcGuess - minOffset * 60000;

    return { date: new Date(correctedMs), wasRounded: true };
  }

  /**
   * Returns the calendar date (year, 1-based month, day) that `timeZone` displays `daysOffset`
   * days from `reference` (default: now), computed by reading `reference`'s zoned calendar date
   * via {@link GetZonedDateComponents} and then shifting by whole days in UTC-anchored arithmetic
   * (so the shift itself is never affected by any DST transition — only the final wall-clock
   * components, read back out, reflect the zone's actual calendar).
   *
   * @param daysOffset Whole days to shift from `reference`'s zoned calendar date (negative for
   * past dates, 0 for the zone's current date, positive for future dates).
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @param reference Instant whose zoned calendar date is the starting point (defaults to now).
   * @returns The shifted calendar date (year, 1-based month, day) in `timeZone` terms.
   */
  GetZonedCalendarDate(
    daysOffset: number,
    timeZone: string,
    reference: Date = new Date()
  ): { year: number; month: number; day: number } {
    const c = this.GetZonedDateComponents(reference, timeZone);
    // Zone-oblivious calendar shift: Date.UTC normalizes day/month/year overflow without any
    // DST influence, so the result is not run back through GetZonedDateComponents.
    const shifted = new Date(Date.UTC(c.year, c.month - 1, c.day + daysOffset));

    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
    };
  }

  /**
   * Resolves `timeZone`'s midnight (00:00:00) on the calendar date `daysOffset` days from
   * `reference`, as a real UTC instant — DST-gap-aware via {@link ResolveDstGap} (a midnight that
   * falls inside a spring-forward gap is rounded forward to the first valid instant, consistent
   * with this app's established gap policy).
   *
   * @param daysOffset Whole days to shift from `reference`'s zoned calendar date, see
   * {@link GetZonedCalendarDate}.
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @param reference Instant whose zoned calendar date is the starting point (defaults to now).
   * @returns The UTC instant of 00:00:00 in `timeZone` on the target calendar date (or the first
   * valid instant after it, if midnight falls inside a spring-forward gap).
   */
  GetZonedStartOfDay(daysOffset: number, timeZone: string, reference?: Date): Date {
    const date = this.GetZonedCalendarDate(daysOffset, timeZone, reference ?? new Date());

    // wasRounded is intentionally ignored: for shortcut/default boundaries there is no
    // user-typed time to warn about, so silently rounding past a gap is the correct behavior.
    return this.ResolveDstGap({ ...date, hour: 0, minute: 0, second: 0 }, timeZone).date;
  }

  /**
   * Resolves `timeZone`'s end of day (23:59:59 or 23:59:59.999, per `ms`) on the calendar date
   * `daysOffset` days from `reference`, as a real UTC instant — DST-gap-aware via
   * {@link ResolveDstGap}.
   *
   * @param daysOffset Whole days to shift from `reference`'s zoned calendar date, see
   * {@link GetZonedCalendarDate}.
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @param ms Milliseconds component: 0 (matches this app's existing `23:59:59.000` shortcut
   * convention) or 999 (matches sites that want an inclusive end just under the next midnight).
   * @param reference Instant whose zoned calendar date is the starting point (defaults to now).
   * @returns The UTC instant of 23:59:59(.ms) in `timeZone` on the target calendar date.
   */
  GetZonedEndOfDay(daysOffset: number, timeZone: string, ms: 0 | 999, reference?: Date): Date {
    const date = this.GetZonedCalendarDate(daysOffset, timeZone, reference ?? new Date());

    // wasRounded is intentionally ignored, see GetZonedStartOfDay.
    const resolved = this.ResolveDstGap(
      { ...date, hour: 23, minute: 59, second: 59 },
      timeZone
    ).date;

    // ResolveDstGap is second-granular, so the milliseconds are added after resolution.
    return new Date(resolved.getTime() + ms);
  }

  /**
   * Resolves the start (`edge: 'start'`) or end (`edge: 'end'`, 23:59:59) of the calendar month
   * `monthOffset` months from `reference`'s zoned "now", as a real UTC instant. `monthOffset: 0`
   * with `edge: 'end'` is "the last day of the current month" (used for a system-wide
   * max-selectable date); `monthOffset: -1` with `edge: 'start'` is "the first instant of last
   * month" (used for rolling month-boundary queries). DST-gap-aware via {@link ResolveDstGap}.
   *
   * @param monthOffset Whole months to shift from `reference`'s zoned calendar month (negative
   * for past months, 0 for the zone's current month, positive for future months).
   * @param edge 'start' for 00:00:00 on the 1st of the target month; 'end' for 23:59:59.000 on
   * the last day of the target month.
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @param reference Instant whose zoned calendar month is the starting point (defaults to now).
   * @returns The UTC instant of the requested month boundary in `timeZone`.
   */
  GetZonedMonthBoundary(
    monthOffset: number,
    edge: 'start' | 'end',
    timeZone: string,
    reference?: Date
  ): Date {
    const ref = reference ?? new Date();
    const c = this.GetZonedDateComponents(ref, timeZone);

    // For 'end', day 0 of the following month normalizes to the last day of the target month
    // (handles 28/29/30/31-day months and year rollover via Date.UTC overflow).
    const boundary =
      edge === 'start'
        ? new Date(Date.UTC(c.year, c.month - 1 + monthOffset, 1))
        : new Date(Date.UTC(c.year, c.month - 1 + monthOffset + 1, 0));

    const date = {
      year: boundary.getUTCFullYear(),
      month: boundary.getUTCMonth() + 1,
      day: boundary.getUTCDate(),
    };

    // wasRounded is intentionally ignored, see GetZonedStartOfDay.
    return edge === 'start'
      ? this.ResolveDstGap({ ...date, hour: 0, minute: 0, second: 0 }, timeZone).date
      : this.ResolveDstGap({ ...date, hour: 23, minute: 59, second: 59 }, timeZone).date;
  }

  /**
   * Resolves the same "Yesterday / Today / Last 7 Days / Last 30 Days"-style shortcut range this
   * app's filter panels already support (see DateSelectionPanelComponent.onShortcutClick and
   * DateRangeSelectorComponent.applyShortcut for the pre-existing, duplicated diff-to-range mapping
   * this method centralizes): `diff > 0` → today through `diff` days ahead; `diff === -1` →
   * yesterday only; `diff === 0` → today only; `diff < 0` (other) → the `-diff` days up to and
   * including today. Both bounds are resolved as real, DST-gap-aware UTC instants in `timeZone`.
   *
   * @param diff Shortcut value (e.g. -1 Yesterday, 0 Today, -7 Last 7 Days, -30 Last 30 Days,
   * or a positive number of days ahead).
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @param ms End-of-day milliseconds component, see {@link GetZonedEndOfDay}.
   * @param reference Instant treated as "now" for both bounds (defaults to now).
   * @returns `start`: 00:00:00 on the first day of the range; `end`: 23:59:59(.ms) on the last
   * day of the range — both as UTC instants in `timeZone` terms.
   */
  GetZonedShortcutRange(
    diff: number,
    timeZone: string,
    ms: 0 | 999,
    reference?: Date
  ): { start: Date; end: Date } {
    // Resolve "now" once so start and end can never straddle a midnight.
    const ref = reference ?? new Date();

    const startOffset = diff > 0 ? 0 : diff === -1 ? -1 : diff === 0 ? 0 : diff + 1;
    const endOffset = diff > 0 ? diff : diff === -1 ? -1 : 0;

    return {
      start: this.GetZonedStartOfDay(startOffset, timeZone, ref),
      end: this.GetZonedEndOfDay(endOffset, timeZone, ms, ref),
    };
  }

  /**
   * Returns the span between two instants in days, measured on `timeZone`'s WALL CLOCK rather
   * than as real elapsed time. Both instants are read through {@link GetZonedDateComponents} and
   * the difference is taken between their components re-anchored in UTC, so any DST offset change
   * between them cancels out.
   *
   * This matters for any "how many days did the user select?" decision (report granularity,
   * date-range limits): a single tenant-local calendar day that contains a DST transition is 25h
   * (fall back) or 23h (spring forward) of real elapsed time, so a raw
   * `(end.getTime() - start.getTime()) / 86400000` reports 1.0417 or 0.9583 days for what the user
   * sees as exactly one day, flipping thresholds such as `> 1` or `> 7`. Measured on the wall
   * clock, one calendar day is always 1 day.
   *
   * Note: {@link GetZonedDateComponents} is second-granular, so a sub-second difference between the
   * two instants is truncated. This is immaterial for day-count thresholds (the app's range ends
   * are 23:59:59(.000/.999), which land on 0.99999 days either way).
   *
   * @param start The instant the range starts at.
   * @param end The instant the range ends at.
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @returns `end - start` expressed in days of `timeZone` wall-clock time (negative if `end`
   * precedes `start`), e.g. 0.99999 for a full single calendar day regardless of DST.
   */
  GetZonedWallClockDaySpan(start: Date, end: Date, timeZone: string): number {
    const s = this.GetZonedDateComponents(start, timeZone);
    const e = this.GetZonedDateComponents(end, timeZone);

    const startMs = Date.UTC(s.year, s.month - 1, s.day, s.hour, s.minute, s.second);
    const endMs = Date.UTC(e.year, e.month - 1, e.day, e.hour, e.minute, e.second);

    return (endMs - startMs) / 86400000;
  }

  /**
   * Returns "the same `timeZone` wall-clock time, `months` calendar months and then `days`
   * calendar days later" as a real UTC instant. Reads `instant`'s zoned components via
   * {@link GetZonedDateComponents}, shifts only the calendar date in zone-oblivious UTC-anchored
   * arithmetic, keeps the zoned hour/minute/second, and resolves the result back to an instant
   * via {@link ResolveDstGap} — so a shift across a DST transition keeps the wall-clock time
   * (e.g. Berlin midnight + 1 day is 23h or 25h of real time on transition days) instead of
   * drifting by the offset change, as a fixed 24h/`setDate` on a browser-local Date would.
   *
   * @param instant The real UTC instant whose zoned wall-clock time is the starting point.
   * @param timeZone IANA time zone id, e.g. "Europe/Berlin", or a fixed offset, e.g. "+0330".
   * @param days Whole calendar days to shift (negative for the past), applied after `months`.
   * @param months Whole calendar months to shift (negative for the past), applied first. Day
   * overflow follows JS `setMonth`/`setDate` semantics (e.g. Jan 31 + 1 month → Mar 3 in a
   * non-leap year).
   * @returns The shifted instant (rounded forward if the target wall-clock time falls inside a
   * spring-forward gap), carrying over `instant`'s milliseconds component.
   */
  ShiftZonedWallClock(instant: Date, timeZone: string, days: number, months: number = 0): Date {
    const c = this.GetZonedDateComponents(instant, timeZone);
    // A single Date.UTC call normalizes the month overflow and then the day overflow exactly like
    // setMonth followed by setDate would, without any DST influence.
    const shifted = new Date(Date.UTC(c.year, c.month - 1 + months, c.day + days));

    // wasRounded is intentionally ignored, see GetZonedStartOfDay.
    const resolved = this.ResolveDstGap(
      {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
        hour: c.hour,
        minute: c.minute,
        second: c.second,
      },
      timeZone
    ).date;

    // ResolveDstGap is second-granular, so the milliseconds are added after resolution.
    return new Date(resolved.getTime() + instant.getMilliseconds());
  }
}
